// Work mode: Next up, Upkeep chips, and the tappable coverage gauge
// (build-plan items 18–21). The first point the app is worth opening in the
// morning.
//
// One thing is offered. "Done" records it; "Not this" moves on and RECORDS
// NOTHING — no event, no penalty, no memory. Declining is not data. If it were,
// the surface would be keeping score, and a person who has to justify a skip
// stops opening the app.
//
// Everything a person reads here is set with textContent, states its reason in
// words (nothing depends on seeing a color, B-01), and every control is a real
// <button> at full target size. Focus is moved deliberately after an action,
// because acting removes the control that was acted on.

import type { Session } from './session.ts';
import type { AppEvent, NodeKind } from '../events.ts';
import { isAppClock, type NodeState } from '../fold.ts';
import { coverageProof, heldWork, whyCovered, type CoverReason } from '../gate.ts';
import { workSurface, type NextUpItem, skipWords } from '../nextup.ts';
import { offerNow, offerWords } from '../offer.ts';
import { loadWords } from '../load.ts';
import { PLAIN_MODULE, PLAIN_HIDDEN, plainIsOn } from '../plain.ts';
import { fitsWith, getWithNow } from '../people.ts';
import { comingBack, furtherOut, undatedCount } from '../held.ts';
import { servesNode } from '../serves.ts';
import { pressureWords } from '../pressure.ts';
import { captureContextWords } from '../capture-context.ts';
import { calendarDaysBetween, atMidnight} from '../time.ts';
import { biteEvents } from './work-intents.ts';
import { ulid } from '../ids.ts';
import { treeRows } from '../tree-view.ts';
import { nodeWords } from '../kind-words.ts';
import { nextFixedToday, nextFixedWords, datedTodayCount, datedWords } from '../clock.ts';
import { boundaryOf } from '../day.ts';
// ONE SOURCE FOR THE EMPTY SENTENCE. `print.ts` already renders this from
// `today.ts`; this file had the same words typed out, so a change to either
// would have made the app and the printed copy disagree about the one line
// somebody sees when there is nothing to do.
import { EMPTY_WORDS } from '../today.ts';
import { getWhereNow, fitsHere, contextNames } from '../contexts.ts';
import { getHowLong, fitsWithin } from '../duration.ts';
import { openSheet, onSheetOpen, wireSheetClose, sheetOpen, closeSheet } from './sheets.ts';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
};

/** Marking a thing done. Gated — completing a one-off can orphan its parent, so
 *  the gate may attach a cure, and it is the gate's business either way. */
export const doneEvents = (
  ctx: { id: () => string; vault: string; at: string; device: string; seq: () => number },
  node: string,
): AppEvent[] => [{
  id: ctx.id(), vault: ctx.vault, at: ctx.at, device: ctx.device, seq: ctx.seq(),
  kind: 'done.marked', node, payload: { at: ctx.at },
} as AppEvent];

export interface WorkUI { refresh(): void }

export function mountWork(
  session: Session, now: () => number, onChange: () => void,
  /** Opens the detail sheet (1.6.0 — the dead lists became doors). Optional,
   *  like clarify's: rows render regardless, doors need the sheet. */
  openDetail?: (n: NodeState) => void,
  /** Opens the load entry with this item attached (1.24.0). Optional and
   *  late-bound, because the load surface mounts after this one — the offer
   *  renders either way, and a missing load entry costs one control. */
  attachLoad?: (nodeId: string, title: string) => void,
): WorkUI {
  const q = <T extends HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);
  const region = q('#nextup');
  const heading = q('#nextup-heading');
  const title = q<HTMLButtonElement>('#nextup-title');
  const why = q('#nextup-why');
  const doneBtn = q<HTMLButtonElement>('#nextup-done');
  const skipBtn = q<HTMLButtonElement>('#nextup-skip');
  const count = q('#nextup-count');
  const behind = q('#nextup-behind');
  const live = q('#nextup-live');
  const upkeepRegion = q('#upkeep');
  const chips = q('#upkeep-chips');
  const gauge = q<HTMLButtonElement>('#gauge');
  const coverage = q('#coverage');
  // Optional, like every other handle here: a missing element costs the panel
  // nothing rather than taking the surface down.
  const coverageCount = q('#coverage-count');
  const coverageProofEl = q('#coverage-proof');
  if (!region || !heading || !title || !why || !doneBtn || !skipBtn || !count ||
      !behind || !live || !upkeepRegion || !chips || !gauge || !coverage) {
    return { refresh() {} };
  }
  const REGION = region, HEADING = heading, TITLE = title, WHY = why, COUNT = count,
    BEHIND = behind, LIVE = live, UPKEEP = upkeepRegion, CHIPS = chips,
    GAUGE = gauge, COVERAGE = coverage;
  // Soft, like LOADNOTE below: a missing place line costs one line of lineage,
  // never the work surface.
  const PLACE = q('#nextup-place');
  const ALSO = q('#nextup-also');
  const WRITTEN = q('#nextup-written');

  /** Which node WRITTEN currently describes, so a repaint for the SAME card
   *  does not blank a correct line — the defect 1.35.0 fixed on the triage
   *  side, avoided here rather than rediscovered. */
  let writtenFor: string | null = null;

  /**
   * WHEN THIS ONE WAS WRITTEN, filled from the log after the card renders.
   *
   * Never blocks: the card is painted from state, synchronously, and this
   * arrives afterwards or not at all (ADR-0001 — nothing on the path to a first
   * capture waits on a store read). A store that is slow or broken costs a line
   * of gray text, never the item somebody was deciding about.
   *
   * The identity guard is the other half: a lookup resolving after the offer has
   * moved on would attach one item's history to another item's title, which is
   * worse than saying nothing.
   */
  const paintWritten = (nodeId: string | null): void => {
    if (!WRITTEN) return;
    if (nodeId !== writtenFor) {
      WRITTEN.textContent = '';
      WRITTEN.hidden = true;
      writtenFor = nodeId;
    }
    if (!nodeId) return;
    void session.store.firstEventFor(nodeId)
      .then((first) => {
        if (!first || writtenFor !== nodeId || !WRITTEN) return;
        const words = captureContextWords(first.at, session.zone, new Date().toISOString());
        if (!words) return;
        WRITTEN.textContent = words;
        // AND NOT WHILE THE STRIP IS ON (2.10.0). This resolves a store lookup,
        // so it lands in a LATER TICK than `refresh` — and the strip's own
        // comment says it "has to be the last word", which it is within the
        // pass and is not against an async painter. `#nextup-written` was in
        // PLAIN_HIDDEN and still on screen, because this line un-hid it a few
        // milliseconds after the strip had hidden it.
        //
        // The class is the live answer rather than a captured flag: the mode can
        // be left while this promise is in flight, and a stale `false` would
        // leave the line gone on an ordinary day.
        WRITTEN.hidden = REGION.classList.contains('nextup-plain');
      })
      .catch(() => { /* a line of context, never the card */ });
  };
  const APPROACH = q('#nextup-approach');
  const SITUATION = q('#nextup-situation');
  // NOT in the hard guard above, deliberately: a missing load note costs one
  // sentence, and taking Next up down with it would cost the app's whole
  // purpose. Same containment every optional element on this surface gets.
  const LOADNOTE = document.querySelector<HTMLElement>('#nextup-load');
  const FIXED = document.querySelector<HTMLElement>('#nextup-fixed');
  const DATED = document.querySelector<HTMLElement>('#nextup-dated');
  // The two things you can do when you cannot start (1.24.0). Soft-bound like
  // LOADNOTE and PLACE: a missing control costs that control, never the offer.
  const BITE = q('#nextup-bite');
  const BITE_FORM = q<HTMLFormElement>('#nextup-bite-form');
  const BITE_INPUT = q<HTMLInputElement>('#nextup-bite-input');
  const BITE_DONE = q<HTMLButtonElement>('#nextup-bite-done');
  const HEAVY = q<HTMLButtonElement>('#nextup-heavy');
  const BITE_OPEN = q<HTMLButtonElement>('#nextup-bite-open');
  /** Has the reader asked for the smaller-step field on the offer in front of
   *  them? Memory only, and cleared when the offer changes — a question that
   *  survived the item it was about would be the app asking again about
   *  something else. */
  let askedForBite = false;

  // "Not this" lives HERE, in memory, and nowhere else. It is deliberately not
  // persisted: a skip that survived a reload would be a record of a decision the
  // app promised not to keep.
  let cycle = 0;
  let current: NextUpItem | null = null;
  /** The first step named under the head, if there is one. Read from state on
   *  every render — never remembered across one, because the head can change
   *  under it and a bite belonging to yesterday's card is the worst thing this
   *  surface could show. */
  let bite: NodeState | null = null;
  // One write at a time. Without this a double-tap wrote done.marked twice for
  // the same node — the log recording an action the user took once, twice.
  // Capture solves the same double-tap by clearing its input synchronously
  // (app.ts); this surface has no input to clear, so it holds a flag.
  let busy = false;
  /** Ids declined this session. In memory only — nothing about a skip is ever
   *  written down, which is the whole point (ADR-0030). */
  const declined = new Set<string>();

  /**
   * ONE REGION, AND THIS SURFACE KEEPS ITS OWN (ADR-0126).
   *
   * WHAT THIS COMMENT USED TO SAY, because the reasoning was sound and has
   * expired: "#nextup-live is visually-hidden, so a sighted user tapped Done,
   * saw nothing change, and had no way to learn the write failed — while
   * capture puts the identical failure in the visible #status. Say it in both
   * places." That was true and it was the fix for F-08.
   *
   * 3.24.5 ended it. `#nextup-live` carries `.receipt` now and is visible on
   * the page, so the second write bought nothing a reader could see and cost
   * what it always cost: `#status` is `aria-live` too, so a screen reader heard
   * every one of these five failures twice. The premise outlived the defect and
   * the parameter outlived the premise.
   *
   * NO `alsoVisible`, AND NO `#status` ROUTE FROM THIS SURFACE AT ALL. Every
   * sentence here — the two Done confirmations, Stopped, the plain-mode pair,
   * the skip sentence, Put-under, and all five failures — is written while this
   * region is still on the page: `refresh` sets `REGION.hidden = false`
   * whenever `settled !== null`, so finishing or stopping shows a settled card
   * rather than taking the surface away. The act never removes what carries the
   * sentence, so by ADR-0126's rule the local region is the only place it goes.
   */
  const say = (msg: string): void => { LIVE.textContent = msg; };

  const nowIso = (): string => new Date(now()).toISOString();

  /** Put focus somewhere real after an action removes the control it was on. The
   *  region hides precisely BECAUSE the last item was completed, so the guard has
   *  to have an else — without one, finishing the last thing stranded focus on
   *  <body> (WCAG 2.4.3). clarify.ts already did this correctly; this file did
   *  not copy it across, and neither did the a11y gate. */
  const restoreFocus = (): void => {
    if (!REGION.hidden) HEADING.focus();
    else document.querySelector<HTMLElement>('#capture')?.focus();
  };

  /**
   * The first step under the head, if one has been named (1.24.0).
   *
   * READ FROM STATE EVERY RENDER, never remembered. The head changes on a skip,
   * a completion, a clock arriving — and a bite left over from the previous
   * card would be this surface telling somebody the first step of a thing they
   * are no longer looking at.
   *
   * The FIRST live, unfinished child, not a list. If somebody names two, the
   * card shows the one they named first and the rest are in the sheet: a card
   * that grows a list of sub-steps has become the pile in miniature, on the one
   * surface whose promise is that it has already chosen.
   */
  const paintBite = (head: NodeState | null): void => {
    bite = null;
    if (head) {
      const st = session.state();
      for (const n of st.nodes.values()) {
        if (n.parent !== head.id) continue;
        if (n.trashed || n.mergedInto || n.lastDone) continue;
        bite = n;
        break;
      }
    }
    if (BITE) {
      BITE.textContent = bite ? `First step: ${bite.title || '(untitled)'}` : '';
      BITE.hidden = !bite;
    }
    if (BITE_DONE) BITE_DONE.hidden = !bite;
    // The form goes away once there is a step, and comes back when it is done.
    // Two open invitations to name a first step is a second decision on a
    // surface built to hold one.
    // BEHIND A DOOR SINCE 2.10.1. The form no longer stands open on the card —
    // it was a field, a loud submit and four lines of prose explaining what a
    // first step is, on the thing somebody is trying to begin. `askedForBite`
    // is the reader having pressed "Start smaller", and it is per-offer: the
    // question is about THIS item, so moving on closes it again.
    if (bite || head === null) askedForBite = false;
    if (BITE_FORM) BITE_FORM.hidden = !askedForBite || Boolean(bite) || head === null;
    if (BITE_OPEN) BITE_OPEN.hidden = askedForBite || Boolean(bite) || head === null;
    if (HEAVY) HEAVY.hidden = head === null;
  };

  const markBiteDone = async (): Promise<void> => {
    if (!bite || busy) return;
    busy = true;
    const node = bite.id;
    const label = bite.title;
    try {
      await session.commit(ctx => doneEvents(ctx, node));
      say(`Done: ${label}.`);
      // NO SETTLE HERE, deliberately. A first step is the way INTO the thing in
      // front of you, not a completion of it — settling would take the item away
      // at the exact moment somebody had finally started. The invitation to name
      // the next small step comes back instead, which is what this control is
      // for. Settling belongs to finishing the OFFERED item.
    } catch (err) {
      say(`Couldn’t record that — ${(err as Error).message}`);
    } finally {
      busy = false;
    }
    try { onChange(); refresh(); } catch { /* the next load renders it */ }
    restoreFocus();
  };

  /**
   * THE MOMENT AFTER (1.35.0) — what the surface is doing while nothing is being
   * asked of you.
   *
   * Null means "offering as usual". A string is the name of the thing that was
   * just finished; the empty string is the same settled state reached by saying
   * that is enough, where nothing was finished and so nothing is named.
   *
   * IN MEMORY. It is a fact about this sitting, not about the work, and a
   * settled state that survived a reload would be the app deciding you are still
   * resting tomorrow.
   */
  let settled: string | null = null;

  const paintSettled = (): void => {
    const region = q('#nextup-settled');
    const what = q('#nextup-settled-what');
    const quiet = q('#nextup-settled-quiet');
    if (!region) return;
    region.hidden = settled === null;
    if (settled === null) return;
    // NAMES WHAT HAPPENED AND NOTHING ELSE. No "well done", no count of what is
    // left, no "one less thing" — an approving opinion is still an opinion about
    // the person, and a number here would attach a tally to finishing.
    if (what) what.textContent = settled === '' ? 'Stopped for now.' : `Finished: ${settled}.`;
    if (quiet) {
      quiet.textContent = settled === ''
        ? 'Nothing is asking. It is all still held.'
        : 'Nothing else is being asked of you. The rest is still held.';
    }
  };

  /**
   * JUST ONE THING — painted before anything else, because it decides what the
   * rest of `refresh` is allowed to say.
   *
   * Everything it suppresses is true and useful on an ordinary day. On the day
   * this is for, the offer's own furniture is the load, and information is the
   * cost being cut.
   */
  /**
   * TWO HALVES, and they run at opposite ends of `refresh`. The first version
   * was one call at the top and was wrong in both directions.
   *
   * Hiding early does not work: almost everything in `PLAIN_HIDDEN` is painted
   * LATER in the same pass by whatever owns it, so the chips were hidden and
   * then shown again a few lines down. The strip has to be the last word.
   *
   * And hiding without a restore does not work either: `#nextup-why` has no
   * other owner of its `hidden` flag, so once this had hidden it, leaving the
   * state left it hidden for ever. The restore runs FIRST, before each element's
   * own rule, so the ordinary pass decides what is visible and the strip only
   * ever overrides it while the state is on.
   */
  /**
   * THE CHROME GOES TOO (2.10.0). The strip only ever reached inside the card,
   * and a mode for the day when operating the tool is itself hard that leaves
   * the tool's own furniture alone answers the smaller half of the problem.
   *
   * It was restored the same way as the card's own — shown back and marked, so
   * each element's own rule could still decide — because `#clock` and
   * `#capture-room` both have owners that hide them for other reasons: the clock
   * is off unless switched on, and the room button is absent where the browser
   * cannot read a clipboard. Restoring either one on the way out is how a
   * control ends up on for somebody who turned it off.
   *
   * That bookkeeping is gone as of 2.14.0 and the hazard with it, which is the
   * argument for the mechanism below rather than a detail of it.
   */
  /**
   * AND IT IS A STYLESHEET NOW, NOT A LOOP OVER `hidden` (2.14.0).
   *
   * The three selectors this list held until 2.14.0 were chrome nothing
   * repaints — a clock, a Contents button, a capture accessory — so setting
   * `hidden` on them from anywhere in the pass worked. The list is the whole
   * work surface now, and every section on it has an owner: `replan`, `focus`,
   * `reentry`, `bother`, `search` and `sort` all paint in `rerenderLists()`,
   * `paintJump` paints `#to-held` deliberately AFTER `work.refresh()` because it
   * has to read what everything else did, and `triage.refresh()` is called from
   * two places that are not in the refresh chain at all.
   *
   * So there is no "last word" to hold. Any ordering that works today is one
   * call site away from silently not working, which is this repo's oldest
   * defect wearing its newest hat — and the failure is invisible, because the
   * source still says the surface is stripped.
   *
   * A rule cannot be outrun by a repaint. Every owner may set `hidden` as often
   * as it likes; while the attribute is on the body the elements are not
   * displayed, and when it comes off each one is exactly where its own rule left
   * it. There is no bookkeeping to restore, and so no way for the restore to
   * hand back something that was never there — which the mark-and-restore
   * version could do, and would have done to `#clock` for anybody who had
   * switched it off.
   *
   * AND THE RULE IS IN `app.css`, NOT INJECTED. The first version of this built
   * a `<style>` element from the list at mount, so there could be exactly one
   * copy of the selectors. The app's CSP is `style-src 'self'` and refused it —
   * correctly, and the browser said so in the console while the mode went on
   * stripping nothing. A measurement caught it; reading the source would not
   * have, because the source was right.
   *
   * So the block in `app.css` is a GENERATED ARTEFACT of this list, the same as
   * CHANGELOG.md and the pre-commit hook are artefacts of their sources:
   * `node tools/plain.mjs --write` writes it and the gate fails on any drift.
   * A second copy held by a gate is not the same object as a second copy nobody
   * checks, which is what this list's own history is about.
   */

  const plainRestore = (): void => {
    for (const sel of PLAIN_HIDDEN) {
      const el = q<HTMLElement>(sel);
      // Shown back unconditionally, then each element's own rule runs later in
      // this same pass and hides it again if it should be hidden. This must not
      // try to be clever about which ones have an owner: guessing that is how a
      // control ends up permanently gone.
      if (el) el.hidden = false;
    }
  };

  const plainStrip = (): void => {
    for (const sel of PLAIN_HIDDEN) {
      const el = q<HTMLElement>(sel);
      if (el) el.hidden = true;
    }
  };

  const paintPlainChrome = (on: boolean): void => {
    if (on) document.body.dataset.plain = '1';
    else delete document.body.dataset.plain;
    REGION.classList.toggle('nextup-plain', on);
    const bar = q('#nextup-plain-bar');
    if (bar) bar.hidden = !on;
    // The way IN goes while it is on, because it is already on — a control that
    // does nothing is worse than absent, and there is one fewer thing to read.
    const onBtn = q<HTMLButtonElement>('#nextup-plain');
    if (onBtn) onBtn.hidden = on;
  };

  const markDone = async (): Promise<void> => {
    if (!current || busy) return;
    busy = true;
    const node = current.node.id;
    const label = current.node.title;
    try {
      await session.commit(ctx => doneEvents(ctx, node));
      say(`Done: ${label}.`);
      // SETTLE, rather than paint the next offer into the space just vacated.
      // The most pressured remaining thing used to arrive instantly, so finishing
      // one thing enrolled you in the next — and whatever occupies the second
      // after completing is what gets attached to completing.
      settled = label || '(untitled)';
    } catch (err) {
      say(`Couldn’t record that — ${(err as Error).message}`);
    } finally {
      busy = false;
    }
    // A render bug must not contradict a landed write (the lesson app.ts records).
    try { onChange(); refresh(); } catch { /* the next load renders it */ }
    restoreFocus();
  };

  /**
   * THAT IS ENOUGH FOR NOW — the symmetric exit.
   *
   * Declining has to end the session as completely as finishing does. "Not this"
   * only ever swapped one demand for another, so the only clean way out of this
   * surface was to complete something: escape strictly dominated, and the
   * interface had chosen for you.
   *
   * Records NOTHING. No event, no count of how often, no memory across a reload.
   * A durable record of when somebody stopped is a record of stopping, which is
   * the shape this app refuses everywhere else.
   */
  const enough = (): void => {
    settled = '';
    refresh();
    say('Stopped for now. Nothing is asking.');
    restoreFocus();
  };

  const resume = (): void => {
    settled = null;
    refresh();
    restoreFocus();
  };

  const setPlain = (on: boolean): void => {
    void session.commit(ctx => [{
      id: ctx.id(), vault: ctx.vault, at: ctx.at, device: ctx.device, seq: ctx.seq(),
      kind: on ? 'module.enabled' : 'module.disabled', node: null,
      payload: { module: PLAIN_MODULE },
    } as never])
      .then(() => {
        // Says what the SCREEN is doing. Never "for when things are hard", which
        // would be the app naming a state of the person.
        say(on ? 'One thing at a time. Everything is still here.' : 'Everything is back.');
        onChange();
        refresh();
        restoreFocus();
      })
      .catch((err: Error) => { say(`Couldn’t do that — ${err.message}`); });
  };

  const skip = (): void => {
    // Remember WHICH items were declined, not how many times. A numeric index
    // over a changing queue threw the user back to the top the moment anything
    // completed, and handed them the item they declined first.
    if (current) declined.add(current.node.id);
    // WHAT WAS ON SCREEN BEFORE THE REFRESH (3.23.28). `current` is reassigned
    // by `refresh()` below, so reading it afterwards and calling the result
    // "instead" claims a swap that may not have happened: with one candidate
    // left the refresh hands back THE SAME ITEM, and the app announced the
    // thing just declined as its own replacement. A cold read pressed *Not
    // this* four times and was told that four times.
    //
    // The tour promises this control "moves on, as often as you like, and
    // records nothing at all". The recording half was always honest — nothing
    // is written here, and `declined` is memory that dies with the surface. It
    // was the MOVING ON that was being claimed without having happened.
    const before = current?.node.id ?? null;
    cycle += 1;
    refresh();
    // Three states, because there are three: a different thing, the same thing
    // still leading because nothing else qualifies, and nothing at all. The
    // middle one used to be told as the first.
    say(skipWords(before, current ? { id: current.node.id, title: current.node.title } : null));
    restoreFocus();
  };

  doneBtn.addEventListener('click', () => void markDone());
  q<HTMLButtonElement>('#nextup-enough')?.addEventListener('click', enough);
  q<HTMLButtonElement>('#nextup-plain')?.addEventListener('click', () => setPlain(true));
  q<HTMLButtonElement>('#nextup-plain-off')?.addEventListener('click', () => setPlain(false));
  q<HTMLButtonElement>('#nextup-resume')?.addEventListener('click', resume);
  BITE_DONE?.addEventListener('click', () => void markBiteDone());
  skipBtn.addEventListener('click', skip);

  // NAME A FIRST STEP (1.24.0, docs/nd-collisions.md entry 1). The whole act is
  // one field and a submit, on the card, because the moment this helps is the
  // moment leaving the surface to do it is more than somebody can spend.
  BITE_OPEN?.addEventListener('click', () => {
    askedForBite = true;
    if (BITE_FORM) BITE_FORM.hidden = false;
    BITE_OPEN.hidden = true;
    // Focus follows, or the door opens a field somebody then has to go and find.
    BITE_INPUT?.focus();
  });

  BITE_FORM?.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!current || busy || !BITE_INPUT) return;
    const text = BITE_INPUT.value;
    // Said out loud rather than committing nothing quietly — capture's rule.
    if (!text.trim()) { say('It needs to say something.'); BITE_INPUT.focus(); return; }
    busy = true;
    const parent = current.node.id;
    void session.commit(ctx => biteEvents(ctx, ulid(Date.parse(ctx.at)), parent, text))
      .then(() => {
        // Cleared only after the write has LANDED (ADR-0008), like capture: an
        // input that empties before the commit resolves can lose what you typed.
        BITE_INPUT.value = '';
        say('Put under it. It takes no date of its own.');
      })
      .catch((err: Error) => { say(`Couldn’t do that — ${err.message}`); })
      .finally(() => {
        busy = false;
        try { onChange(); refresh(); } catch { /* the next load renders it */ }
      });
  });

  // SAY IT IS HEAVY (entry 2). Not a second form — it opens the one under the
  // capture line with this item attached, so the weight is recorded exactly as
  // it always has been and finally says what it is about.
  HEAVY?.addEventListener('click', () => {
    if (!current || !attachLoad) return;
    attachLoad(current.node.id, current.node.title || '(untitled)');
  });

  // THE CLAIM IS A PLACE, NOT A FOLD (2.0.5, ADR-0088).
  //
  // This unfolded under the gauge, directly above the held list. At 523 held
  // things that is 26,031px — twenty-two screens of claim inserted between the
  // reader and the list they were looking at, measured at 820x1180. The rows
  // still build on open and only on open, for the reason `buildCoverage` gives;
  // what changed is that "open" now means a surface arrived at rather than a
  // fold pushing the workspace down.
  //
  // Registered rather than called: `openSheet` runs the repaint, so the sheet
  // cannot be reached by any other door — a deep link, the walk — and show the
  // state the app was in when it started.
  onSheetOpen('sheet-coverage', () => { buildProof(); buildCoverage(); });
  wireSheetClose('sheet-coverage');
  // The offered card opens its own sheet (2.2.0, ADR-0092). `current` is the
  // item the surface is showing, so this can never open the wrong thing —
  // and if the offer is empty there is nothing to open.
  /**
   * A CONTROL THAT CANNOT ACT IS NOT A CONTROL, and this one looked exactly
   * like the openable title because it IS the openable title — same element,
   * `--ink` at 1.375rem, underlined, 44px tall, with an accent underline on
   * hover. On the branch that says "Nothing is asking today." there is nothing
   * to open, so the guard below returned and the tap did nothing at all. The
   * seventh cold read met it as a dead end; from the reader's side a title that
   * is underlined and silent is indistinguishable from a broken one.
   *
   * `disabled` rather than a second element: it takes the control out of the
   * tab order, stops a screen reader offering it, and drops the hover
   * underline through the one rule added beside `.nextup-title` — which pins
   * `--ink` and `opacity: 1` deliberately, so the sentence keeps the exact
   * foreground the registry already measures and no new pair arrives with the
   * fix.
   */
  TITLE.addEventListener('click', () => {
    if (!current || !openDetail) return;
    const fresh = session.state().nodes.get(current.node.id);
    if (fresh) openDetail(fresh);
  });

  GAUGE.addEventListener('click', () => { openSheet('sheet-coverage'); });

  /** Plain words for when something returns — calendar days in the reader's
   *  zone, never a countdown and never a rebuke. */
  const returns = (iso: string): string => {
    const d = calendarDaysBetween(nowIso(), iso, { zone: session.zone, boundary: boundaryOf(session.state()) });
    if (d < 0) return 'ready now';
    if (d === 0) return 'today';
    if (d === 1) return 'tomorrow';
    if (d < 7) return `in ${d} days`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: session.zone });
  };

  function refresh(): void {
    const state = session.state();
    const iso = nowIso();
    // workSurface removes the chip items from Next-up, so a ready upkeep is not
    // rendered twice on one screen with two Done buttons writing to one node.
    const { up: all0, chips: ups } = workSurface(state, iso, session.zone, 0);
    // WHERE YOU ARE (2.2.0, ADR-0092). The half that answers the question this
    // feature exists for: "show me what I can do at home" is useless if the ONE
    // thing the app hands you is a job for the office.
    //
    // Filtered here rather than inside `workSurface` deliberately — the ranking
    // and every test over it stay untouched, exactly as ADR-0060 kept `nextup`
    // untouched when the offer's shape changed. This narrows what is OFFERED
    // from that ranking; it does not re-rank anything.
    //
    // Law 1 is not bent: what is filtered out still has its clock, still counts
    // in the claim, and still comes back. Unlabeled things fit anywhere.
    const here = getWhereNow();
    // `NextUp` is head + behind + total, not an array. Filtering has to keep
    // that shape honest: if the head does not fit, the first thing behind that
    // does becomes the head, and `total` counts what fits — a total that
    // counted things you cannot do from here would be the surface answering a
    // different question from the one the chooser asked.
    //
    // AND HOW LONG YOU HAVE (2.19.0, the plan's phase 3). The same treatment
    // and in the same place, because it is the same kind of narrowing: what is
    // filtered out still has its clock, still counts in the claim, and still
    // comes back. An unestimated thing fits every answer, exactly as an
    // unlabeled thing fits anywhere.
    //
    // THE OFFER IS THE HALF THAT MATTERS. Narrowing only the held list would
    // leave the one thing the app actually hands you unfiltered — "I have
    // twenty minutes" answered with a two-hour job is the feature not working,
    // which is the reasoning `getWhereNow` already carries about place.
    const within = getHowLong();
    const withWhoNow = getWithNow();
    const all = ((): typeof all0 => {
      if (!here && within === null && !withWhoNow) return all0;
      // WHO IS HERE joins the composition (2.26.0, entry 24's third axis, and
      // the best evidenced of the three). Three predicates, composed, over the
      // ONE offer — never three screens. That guardrail is the entry's own and
      // the other two candidates depend on it: building any axis as its own
      // destination is the same mistake regardless of which axis it is.
      const fits = (i: NextUpItem) => fitsHere(state, i.node, here)
        && fitsWithin(i.node, within) && fitsWith(state, i.node, withWhoNow);
      const kept = [...(all0.head ? [all0.head] : []), ...all0.behind].filter(fits);
      return { head: kept[0] ?? null, behind: kept.slice(1), total: kept.length };
    })();
    // THE MENU SHAPE (1.11.0, ADR-0060). What is offered is a small set chosen
    // to be UNALIKE — at most one item per reason, plus one thing off the Menu
    // that owes nothing. `offerNow` owns that rule; this file only renders it.
    const offer = offerNow(state, iso, session.zone, cycle);
    // `offerNow` already applied the one-thing cap — the projection owns the
    // offer's shape. This only needs to know whether to strip the furniture.
    const plain = plainIsOn(state);
    // Prefer something not yet declined this session; if everything has been,
    // start again from the top rather than showing nothing.
    const fresh = offer.work.filter(i => !declined.has(i.node.id));
    const head = fresh[0] ?? offer.work[0] ?? all.head;
    const behind = (fresh.length > 1 ? fresh.slice(1) : offer.work.slice(1))
      .filter(i => i.node.id !== head?.node.id);
    const up = { head, behind, total: all.total };
    current = up.head;

    // SETTLED (1.35.0). The offer, its reason, its behind-list and every control
    // that acts on it are withheld while the surface is settled — not merely
    // grayed. The whole point is that nothing is being asked, and a demand that
    // is present but disabled is still a demand on the screen.
    //
    // `current` is cleared with it, so a stray keypress cannot act on an item
    // the reader cannot see.
    paintPlainChrome(plain);
    // Before anything else paints, so the ordinary rules get the last word on
    // an ordinary pass and `plainStrip` gets it on a plain one.
    if (!plain) plainRestore();
    paintSettled();
    if (settled !== null) {
      // Nothing is being asked, so `current` is cleared with the offer: a stray
      // keypress must not act on an item the reader cannot see.
      current = null;
      TITLE.textContent = '';
      // A BUTTON WITH NO WORDS HAS NO NAME (2.2.0). Since the title became a
      // door it must go when there is nothing to open, not sit there empty.
      TITLE.hidden = true;
      WHY.textContent = '';
      if (PLACE) PLACE.hidden = true;
        if (ALSO) ALSO.hidden = true;
      paintWritten(null);
      if (APPROACH) APPROACH.hidden = true;
      if (SITUATION) SITUATION.hidden = true;
      // The first-step line and its form are demands too, and were left on
      // screen by the first version of this — the walk caught it as a bite that
      // would not go away.
      const biteLine = q('#nextup-bite');
      if (biteLine) biteLine.hidden = true;
      const biteForm = q('#nextup-bite-form');
      if (biteForm) biteForm.hidden = true;
      if (BITE_OPEN) BITE_OPEN.hidden = true;
      const biteDone = q<HTMLButtonElement>('#nextup-bite-done');
      if (biteDone) biteDone.hidden = true;
      BEHIND.replaceChildren();
      COUNT.textContent = '';
      // AND THE DATED COUNT (2.19.1). A settled surface is one where nothing is
      // being asked, and a count of today's dates is a demand however plainly it
      // is worded. Settling IS finishing early, so the question this line
      // answers has already been answered by the act.
      //
      // `#nextup-fixed` deliberately survives this branch and this one does not:
      // that line NAMES one thing still ahead and is an ambient horizon, which
      // is worth having on a settled afternoon. A count is not.
      if (DATED) { DATED.textContent = ''; DATED.hidden = true; }
    }
    // The offer's controls are WITHHELD while settled, not grayed. A demand that
    // is present but disabled is still a demand on the screen.
    for (const sel of ['#nextup-enough', '#nextup-heavy']) {
      const b = q<HTMLButtonElement>(sel);
      if (b) b.hidden = settled !== null || !up.head;
    }
    if (doneBtn) doneBtn.hidden = settled !== null;
    if (skipBtn) skipBtn.hidden = settled !== null;

    // The rest of the surface below — the gauge, the upkeep chips, the held list
    // — is untouched by settling. None of it was asking anything, and hiding it
    // would make "nothing is being asked" look like "nothing is here".
    if (settled !== null) {
      REGION.hidden = false;
    } else if (up.head) {
      REGION.hidden = false;
      // Restored explicitly: they are hidden in the no-head branch below, and a
      // control that disappears once and never returns is the worst of both.
      // (Settled is handled above and cannot reach here.)
      if (doneBtn) doneBtn.hidden = false;
      if (skipBtn) skipBtn.hidden = false;
      TITLE.textContent = up.head.node.title || '(untitled)';
      TITLE.hidden = false;
      TITLE.disabled = false;
      // Why this, in words. Pressure adds its own gentle phrase; neither ever
      // reaches for the shame word this app refuses — no such state exists here,
      // and the vocabulary that replaces it is in pressure.ts (ADR-0010).
      const p = up.head.pressure;
      const extra = up.head.reason === 'pressure' ? pressureWords(p) : up.head.words;
      WHY.textContent = extra;
      // WHERE, right under why (V2 stage 1). Silence for a loose item — "in
      // nothing" is not a location, and announcing bareness would make the flat
      // majority of a fresh store read as incomplete.
      if (PLACE) {
        // AND WHAT IT IS FOR (2.5.0, ADR-0095), appended to the same line rather
        // than given one of its own — this card already carries a why, a where,
        // a when-written, an approach, a situation and a first step, and law 8
        // caps what a surface may show.
        //
        // ONLY WHEN IT ADDS SOMETHING. `lineageOf` walks two hops and the second
        // is the first live container above the parent, so on a two-deep tree
        // ("in Re-do the hallway · under A calmer house") the horizon is ALREADY
        // named and appending "serves A calmer house" would be the card saying
        // one fact twice in two vocabularies. It earns its place on a deeper
        // tree, where the two-hop walk stops below the horizon and the goal goes
        // unnamed. Checked against the rendered string rather than by counting
        // depth, because the string is the thing the reader is comparing it to.
        const forWhat = servesNode(session.state(), up.head.node);
        const already = forWhat !== null && (up.head.place ?? '').includes(forWhat.title || '(untitled)');
        const serves = forWhat === null || already
          ? null : `serves ${forWhat.title || '(untitled)'}`;
        const line = [up.head.place, serves].filter(Boolean).join(' · ');
        PLACE.textContent = line;
        PLACE.hidden = line.length === 0;
      }
      // WHAT ELSE IS IN THERE (nd-collisions entry 3, the thesis's open half).
      //
      // Only on a `beneath` offer, which is the moment a horizon comes round —
      // the projection returns nothing for every other reason, so this is one
      // read of a field rather than a condition to keep in step with a tier.
      //
      // The wording says what it is and asks for nothing. These are named so
      // they are SEEN; an act on any of them would turn a place returning into
      // a queue arriving, which is the thing law 8 caps.
      if (ALSO) {
        const also = up.head.alsoHere ?? [];
        const inWhat = up.head.alsoIn ? `Also in ${up.head.alsoIn}` : 'Also in there';
        ALSO.textContent = also.length === 0 ? '' : `${inWhat}: ${also.join(' · ')}`;
        ALSO.hidden = also.length === 0;
      }
      // WHEN IT WAS WRITTEN (2.0.3) — the triage card has said this since
      // 1.29.0 and this card never did.
      //
      // A title is very often a FRAGMENT: "take the old one to the tip", "ring
      // them back", "order the part". Under a project that reads perfectly and
      // PLACE above says which project. With no parent there is no place line,
      // and the card was then a bare imperative with no subject anywhere on the
      // screen — found on device, offered with a real date and nothing else to
      // go on. `docs/nd-collisions.md` entry 17 is exactly this: the context
      // that made a fragment meaningful lives in working memory and is gone
      // within hours.
      //
      // The information already existed and was already computed — it was shown
      // on ONE surface. Same one writer, same words, so two surfaces cannot
      // describe the same fact two ways.
      //
      // NEVER BLOCKS, and never how long ago. It fills in from the log after
      // the card renders, exactly as the triage line does, and `captureContextWords`
      // refuses an age: "3 weeks old" is the same fact wearing an accusation.
      paintWritten(up.head.node.id);
      // WHAT IT HOLDS UP, under where it sits (1.23.0). The head only: the
      // behind-rows already carry a why and a place, and a third line on each
      // turns the rest-of-offer from a glance into a paragraph — which is the
      // bounded-surface instinct law 8 applies to lists, applied to a card.
      //
      // Silent when null, the same rule PLACE follows, and null is the ORDINARY
      // case: it needs a declared downstream and a lead estimate, both set on
      // the detail sheet. An item with neither says nothing rather than being
      // given a schedule it never had.
      if (APPROACH) {
        APPROACH.textContent = up.head.approach ?? '';
        APPROACH.hidden = !up.head.approach;
      }
      // THE SITUATION, VERBATIM AND UNQUALIFIED. Not "you said you would…",
      // which reads the plan back as a commitment and turns a cue into a
      // reminder that you have not done it — this audience's whole problem with
      // being reminded. Just the sentence, in the words it was written in.
      //
      // Silent when there is none, the same rule PLACE and APPROACH follow. A
      // situation is offered and never required, so absence is the ordinary
      // case and must cost nothing.
      if (SITUATION) {
        SITUATION.textContent = up.head.situation ?? '';
        SITUATION.hidden = !up.head.situation;
      }
      paintBite(up.head.node);
      // THE COUNT LINE MOVED DOWN TO WHERE THE ROWS ARE KNOWN (3.9.1) — see
      // `offerWords`. It was set here, from `offer`, while the list it
      // introduces is built from `up.behind` a hundred lines below. Two
      // computations of the same thing, agreeing by luck until they did not.
      // The visible reason ADR-0014 asks for, in the co-occurrence form law 7
      // requires. Read from the SAME offer the rows were built from, so the
      // sentence and the set can never disagree.
      if (LOADNOTE) {
        const lw = loadWords(offer.load);
        LOADNOTE.textContent = lw;
        LOADNOTE.hidden = lw === '';
      }
      // "THE ONE PERMITTED NUMBER" STOOD HERE AND IS GONE (2.12.2, ADR-0103).
      // It read "About 2h 30m left today." on every ordinary offer, and it was
      // defended as prospective — a fit judgment made before the attempt. The
      // card never carried the other half of that judgment: how long the
      // offered thing takes is `rangeWords`, and `rangeWords` renders only in
      // the detail sheet. A remainder with nothing to measure against is not a
      // fit judgment, it is a countdown — which is the thing the header clock
      // is opt-in to avoid imposing.
      //
      // THE NEXT FIXED THING TODAY, by name (collisions 7 and 9). An absorbed
      // person can catch this peripherally; somebody whose afternoon is being
      // eaten by a 3pm appointment can see what it actually is. No countdown —
      // the OS alarm is the guarantee and this is the ambient horizon.
      //
      // AND NEVER ABOUT THE THING IN FRONT OF YOU. The next fixed thing today is
      // often the thing being offered — a real date today is the first reason
      // `nextUp` ranks on, so the two agree by construction rather than by
      // chance. The card then read "Ring the plumber back about the tap" as its
      // head and "Fixed today: Ring the plumber back about the tap." three lines
      // under it, which is the card telling somebody about the thing they are
      // already looking at, in a release whose whole subject was that the card
      // says too much. Measured across six rotations of the sample: present on
      // all six, naming the head on two of them.
      //
      // This line is an AMBIENT HORIZON — the value in it is that it names
      // something you are NOT looking at (collisions 7 and 9: the afternoon
      // being eaten by a 3pm appointment). When it names the head it has no
      // value left, only length. The focus surface renders the same projection
      // and is untouched: nothing there shows the head, so the line is doing
      // exactly its job.
      if (FIXED) {
        const fixed = nextFixedToday(
          session.state(), nowIso(), { zone: session.zone, boundary: boundaryOf(session.state()) });
        const fw = fixed && fixed.id === up.head.node.id ? null : nextFixedWords(fixed);
        FIXED.textContent = fw ?? '';
        FIXED.hidden = fw === null;
      }
      // WHAT TODAY ACTUALLY COMMITS YOU TO (2.19.1). "What is on my plate,
      // enough to decide whether to finish early" — and the answer that lets
      // somebody stop is the ZERO one, so it is stated rather than left as an
      // absence. `datedWords` was written for exactly this and says so in its
      // own docstring: zero is an ordinary fact, not an achievement and not a
      // reproach.
      //
      // ALWAYS, not only when the clock module is on. This pair has existed
      // since the clock and rendered only inside it — an opt-in ticking face,
      // off by default, with this fact third in a sentence after the time and
      // the remainder. Live code with no route to it, the same shape as the
      // unfed-goal reading phase 2 found.
      //
      // NOT the remainder beside it. "2h 30m left today" is what ADR-0103 took
      // off this card, and it stays off.
      if (DATED) {
        const n = datedTodayCount(
          session.state(), nowIso(), { zone: session.zone, boundary: boundaryOf(session.state()) });
        DATED.textContent = datedWords(n);
        DATED.hidden = false;
      }
      // Doors, not words-in-a-paragraph (1.6.0): each row opens its sheet, on
      // the FRESH node — a row built at refresh time can be clicked later. What
      // sits here is now the REST OF THE OFFER rather than a queue tail: one
      // more piece of work of a different kind, and one thing you wanted.
      const rows = up.behind.map(item => ({
        id: item.node.id,
        title: item.node.title || '(untitled)',
        why: item.reason === 'pressure' ? pressureWords(item.pressure) : item.words,
        place: item.place,
        wish: false,
      }));
      // The wish rides last and says what it is. It owes nothing (law 6), so it
      // never carries a reason, a date, or a word that could read as asking.
      //
      // AND IT NO LONGER NAMES A CATEGORY NOBODY CHOSE (3.23.14). This read
      // "something you wanted · Read", and a cold reader took the second half
      // for a fact about the item — reasonably, since it is printed in the same
      // place and register as every other fact on that row. Nothing had chosen
      // it: `read` is the DEFAULT the gate writes when a `someday` or
      // `reference` route lands something on the Menu (`gate.ts`, and
      // `triage-intents.ts` beside it), so a reader who pressed *Someday* was
      // shown a word they never picked, asserted about their own thing.
      //
      // The Menu screen still groups by category and still needs a bucket to
      // group into, which is what the default is for and why it stays. The
      // difference is that a heading over a group is a filing scheme, and a word
      // after a middot on the thing itself is a claim about the thing.
      if (offer.wish && offer.wish.id !== head?.node.id) {
        rows.push({
          id: offer.wish.id,
          title: offer.wish.title || '(untitled)',
          why: 'something you wanted',
          // The wish line already says everything a wish owes anyone (law 6);
          // a location would dress it as filed work.
          place: null as string | null,
          wish: true,
        });
      }
      // Counted from the rows themselves, which is the whole of the 3.9.1 fix.
      COUNT.textContent = offerWords(rows.length, up.head !== null);
      BEHIND.replaceChildren(...rows.map(row => {
        const li = el('li', row.wish ? 'behind-item behind-wish' : 'behind-item');
        const b = el('button', 'behind-open');
        b.type = 'button';
        b.append(el('span', 'behind-title', row.title));
        b.append(el('span', 'behind-why', row.why));
        if (row.place) b.append(el('span', 'behind-why behind-place', row.place));
        if (openDetail) b.addEventListener('click', () => {
          const node = session.state().nodes.get(row.id);
          if (node) openDetail(node);
        });
        li.append(b);
        return li;
      }));
    } else {
      // NOTHING IS ASKING — and if things are being held without dates, say that
      // too rather than simply vanishing.
      //
      // "Nothing is asking" is true and, on its own, unhelpful: a real import of 1,429
      // undated things and this surface correctly had nothing to offer, which reads
      // as an empty app rather than as a full one waiting on a decision. The section
      // stays, says the real number, and the two action buttons go — there is
      // nothing to be done to, and a live button with no subject is worse than none.
      const undated = undatedCount(session.state(), nowIso(), session.zone);
      // AND WHAT IS COMING BACK, which this branch could not say (3.23.18).
      // The sentence below has always named the UNDATED things; work that IS
      // dated and is not due today was named nowhere, so sorting seven things as
      // *Next action* — which takes tomorrow's clock on purpose — produced
      // "Nothing is asking today" over a count of five other things, with the
      // seven invisible. `held.ts:comingBack` says why this is the fix and the
      // clock is not.
      const coming = comingBack(session.state(), nowIso(), session.zone);
      // AND THE THIRD FACT, which neither of the other two could reach (3.23.29).
      // `comingBack` is the `soon` group — a week at most, by construction — and
      // `undatedCount` asks whether anything is demanding. A date further out than
      // a week answers neither, so three dated projects sat on this screen named
      // nowhere while the coverage sheet counted them. `held.ts:furtherOut` has
      // the measurement and says why this is a sentence rather than a bucket
      // boundary.
      const further = furtherOut(session.state(), nowIso(), session.zone);
      BEHIND.replaceChildren();
      if (doneBtn) doneBtn.hidden = undated > 0;
      if (skipBtn) skipBtn.hidden = undated > 0;
      if (undated > 0 || coming || further) {
        REGION.hidden = false;
        TITLE.textContent = EMPTY_WORDS;
        TITLE.hidden = false;
        TITLE.disabled = true;
        if (PLACE) { PLACE.textContent = ''; PLACE.hidden = true; }
        paintWritten(null);
        // Cleared beside PLACE, for its reason: this branch reuses the same
        // elements with a different sentence, so a line left over from the last
        // head would attach a previous item's downstream to "Nothing is asking".
        if (APPROACH) { APPROACH.textContent = ''; APPROACH.hidden = true; }
        paintBite(null);
        // TWO FACTS, EACH ONLY WHEN IT IS TRUE. The undated half is unchanged. The
        // coming-back half is stated as a fact and never as a demand — it says
        // when, and asks for nothing, because nothing is being asked today and
        // saying otherwise would put a date's weight on a day it does not own.
        const loose = undated === 0 ? ''
          : undated === 1
            ? 'One thing is here without a date. It is waiting on you to decide, not the other way round.'
            : `${undated} things are here without a date. They are waiting on you to decide, not the other way round.`;
        const back = !coming ? ''
          : coming.count === 1
            ? `One thing comes back to you ${coming.words}.`
            : `${coming.count} things come back to you, the first of them ${coming.words}.`;
        // FURTHER OUT THAN THE WEEK, and never "more" — the word only reads as
        // *more* when something came before it, and this clause stands alone
        // whenever nothing is coming back inside the week.
        const far = !further ? ''
          : further.count === 1
            ? `One more has a day further out, ${further.words}.`
            : `${further.count} more have a day further out, the first of them ${further.words}.`;
        const farAlone = !further ? ''
          : further.count === 1
            ? `One thing has a day further out, ${further.words}.`
            : `${further.count} things have a day further out, the first of them ${further.words}.`;
        WHY.textContent = [back, back ? far : farAlone, loose].filter(Boolean).join(' ');
        COUNT.textContent = '';
        if (LOADNOTE) { LOADNOTE.textContent = ''; LOADNOTE.hidden = true; }
        // THE AMBIENT HORIZON IS PAINTED HERE, NOT CLEARED (3.23.18). Every other
        // element in this branch belongs to the offered item, so clearing them is
        // right — a line left from the last head would attach a previous item's
        // downstream to "Nothing is asking". `#nextup-fixed` is not one of those:
        // `nextFixedToday` is a fact about the whole store, and the head branch
        // above hides it only when it names the head, because a line about the
        // thing you are already looking at has no value left.
        //
        // With no head there is nothing for it to duplicate, and this is the
        // state the line is MOST for — nothing is asking, and an appointment at
        // three o'clock is the afternoon being eaten (collisions 7 and 9). It was
        // being wiped instead.
        //
        // AND THE A11Y WALK WAS PASSING ON WHAT THIS LEFT BEHIND. Its
        // ambient-horizon assertion reads `#nextup-fixed` during a focus session,
        // when the work surface has no head — so this branch ran, and before
        // 3.23.18 it ran only when something was undated. When it did not run,
        // whatever the line last said stayed in the DOM and was read back as a
        // pass. Widening this branch's condition removed the stale value and the
        // assertion went red, which is the check finally measuring something.
        if (FIXED) {
          const fixed = nextFixedToday(
            session.state(), nowIso(), { zone: session.zone, boundary: boundaryOf(session.state()) });
          const fw = nextFixedWords(fixed);
          FIXED.textContent = fw ?? '';
          FIXED.hidden = fw === null;
        }
        // The settled branch already says "Nothing is asking today" above, so a
        // second sentence about dates would be the same news twice.
        if (DATED) { DATED.textContent = ''; DATED.hidden = true; }
      } else {
        REGION.hidden = true;
        TITLE.textContent = '';
        TITLE.hidden = true;
      }
    }

    // Upkeep chips (item 20) — already computed above, and already removed from
    // the Next-up queue.
    UPKEEP.hidden = ups.length === 0;
    CHIPS.replaceChildren(...ups.map(item => {
      const li = el('li');
      const b = el('button', 'chip');
      b.type = 'button';
      b.append(el('span', 'chip-title', item.node.title || '(untitled)'));
      b.append(el('span', 'chip-why', pressureWords(item.pressure)));
      if (item.place) b.append(el('span', 'chip-why chip-place', item.place));
      b.addEventListener('click', () => {
        const node = item.node.id;
        void session.commit(ctx => doneEvents(ctx, node))
          .then(() => { LIVE.textContent = `Done: ${item.node.title}.`; })
          .catch((err: Error) => { LIVE.textContent = `Couldn’t record that — ${err.message}`; })
          .finally(() => { onChange(); refresh(); });
      });
      li.append(b);
      return li;
    }));

    // The coverage list (item 21) — built ONLY while it is on screen. It renders
    // one row per held node, and it was being rebuilt hidden on every refresh:
    // at 1,429 held things that is ~4,300 DOM elements constructed and thrown
    // away per keystroke-adjacent repaint, for a list nobody was looking at
    // (audit, measured). The gauge's click handler builds it at the moment of
    // opening, and refresh keeps it live only while open.
    // THE CLAIM IS ALWAYS CURRENT, even while it is hidden. The rows are built
    // only on open — at 1,429 held things, rebuilding them hidden on every
    // repaint is ~4,300 DOM elements thrown away for a list nobody is looking
    // at (audit, measured) — but one string is free, and a count that only
    // refreshes on open is a count that can be STALE the moment it is read,
    // which is worse than absent. Same `heldWork` set the rows come from.
    // THE LAST WORD, after everything that owns one of these has painted.
    // The card's half only. The chrome's half is a stylesheet rule now and does
    // not need a last word — see `mountPlainChromeRule`.
    if (plain) plainStrip();

    paintCoverageCount();
    // WHILE IT IS BEING LOOKED AT — which is now "its sheet is open" rather than
    // "its list is not hidden" (2.0.5). Same rule, same measured reason: rebuild
    // the rows only for a reader who is actually on the surface.
    if (sheetOpen('sheet-coverage')) { buildProof(); buildCoverage(); }
    // The tree obeys the same rule, for the same measured reason.
    if (treeList && sheetOpen('sheet-tree')) buildTree();
  }

  /** How many the claim holds, stated where the reader asked for it. The gauge
   *  itself no longer carries a total: an aggregate on the landing surface is a
   *  number that only rises, and this one answers a question just asked. */
  function paintCoverageCount(): void {
    if (!coverageCount) return;
    const n = heldWork(session.state()).length;
    coverageCount.textContent = n === 1
      ? 'One thing, and when it comes back:'
      : `${n} things, and when each comes back:`;
  }

  /**
   * HOW IT KNOWS — the proof behind the claim (ADR-0084).
   *
   * The thesis this app is built on says the failure is not forgetting, it is
   * that your coverage is unverifiable from the inside. A count cannot answer
   * that: `0 silent` reads the same whether the app is watching everything or
   * watching nothing, and this repo has already shipped a green gauge over items
   * nothing would ever surface. A list of items cannot answer it either — a list
   * shows what is IN the container, never whether the container holds.
   *
   * A REASON can. Somebody holding forty things who sees them accounted for
   * under four named reasons can check those reasons against what they actually
   * put in, from the outside, in one look.
   *
   * And it must be able to say NO. The exceptions are named, because a guarantee
   * with no failure mode is not checkable, and an app that can only ever say
   * "fine" is asking for the exact faith the reader does not have.
   */
  // EACH ONE FOLLOWS A BARE NUMBER, so each one has to read after any number.
  // These were written as plural verbs — "6 have a day they come back to you"
  // is fine and "1 have a day they come back to you" is not, and a reader with
  // one thing under a reason sees only the broken one. Rather than a second
  // table of singulars to keep in step, every phrase is now number-agnostic:
  // it modifies the count instead of agreeing with it.
  // TYPED ON `CoverReason`, NOT `string` (3.23.29). `Record<string, string>` let
  // 3.23.29's own new reason be added to `gate.ts` with no words here, and the
  // sheet would have rendered `undefined` beside a count — on the one surface in
  // this app whose entire job is being checkable from outside. `tools/reasons.mjs`
  // holds the same totality out loud, for the reason its own header gives about
  // the offer's record: a `Record<K, V>` quietly stops being total the moment the
  // key type widens back to `string`.
  const REASON_WORDS: Record<CoverReason, string> = {
    clock: 'with a day they come back to you',
    // THE APP'S OWN MARKER IS NOT A DAY YOU SET (3.23.29). Every undated node
    // carries a `review` cure so that nothing goes silent (law 1), and those were
    // counted under `clock` above — so this line said "with a day they come back
    // to you" about the very things *See what is next* was calling "here without
    // a date", in the same store, at the same moment. The reason a reader would
    // give is the reason to print, and a reader opening this sheet sees these
    // rows reading "held" with no date on them.
    cure: 'held, with no day you have set \u2014 they still come back',
    // FINISHED, AND NOT COMING BACK (3.23.16). These were counted under `clock`
    // and listed as returning today, because the log is append-only and marking
    // a thing done does not erase the day it carried. An upkeep between rounds
    // stays under `clock`, where it belongs — it genuinely does come back.
    done: 'finished, and not waiting to come back',
    menu: 'on the Menu — no clock, because you said so',
    parent: 'coming back with something they are part of',
    after: 'waiting on something that will be shown to you first',
    'demand-free': 'not work, and with a place of their own',
    decided: 'already put down or let go',
  };

  function buildProof(): void {
    if (!coverageProofEl) return;
    const proof = coverageProof(session.state());
    const parts: HTMLElement[] = [];

    // The promise, in one sentence, and it is allowed to be bad news.
    const promise = el('p', proof.holds ? 'proof-holds' : 'proof-broken');
    promise.textContent = proof.holds
      ? 'Everything here comes back to you on its own. You are not the one remembering.'
      : proof.exceptions.length === 1
        ? 'One thing here will not come back on its own.'
        : `${proof.exceptions.length} things here will not come back on their own.`;
    parts.push(promise);

    // How it knows. No total — the sum of a container's reasons is not a score,
    // and `N held` was removed from the gauge on purpose (V2 stage 1).
    const why = el('ul', 'proof-why');
    for (const r of proof.reasons) {
      const li = el('li');
      li.append(el('span', 'proof-count', String(r.count)));
      li.append(el('span', 'proof-reason', REASON_WORDS[r.reason] ?? r.reason));
      why.append(li);
    }
    if (proof.reasons.length > 0) parts.push(why);

    // What it cannot promise, named. Never a count on its own: a number here
    // would be a thing to feel about rather than a thing to act on.
    if (proof.exceptions.length > 0) {
      const bad = el('ul', 'proof-exceptions');
      for (const n of proof.exceptions) {
        bad.append(el('li', undefined, n.title || '(untitled)'));
      }
      parts.push(bad);
    }
    coverageProofEl.replaceChildren(...parts);
  }

  /** The gauge's claim, itemized and checkable. Reads `heldWork` — the same
   *  definition the gauge counts — so opening the claim can never contradict it.
   *
   *  It read `heldNodes` until 1.15.1, which is one word wider and was the wrong
   *  set: every private journal entry has no title by design, so each one
   *  itemized here as "(untitled) — held". */
  /** WHICH CLOCK THIS ROW IS ABOUT, named once (3.19.3).
   *
   *  The row renders one clock and the list now SORTS by one, and if those were
   *  two expressions they would eventually disagree — an item ordered by its
   *  `due` while its row said `returns` from its `suspense` is a list that is
   *  wrong in the one way nobody would think to check. Same rule `src/today.ts`
   *  states about a second definition of "what matters today". */
  /**
   * THE DAY A READER PUT ON THIS ROW, never the app's own marker (3.23.29).
   *
   * `isAppClock` is the filter and it is load-bearing twice over on this sheet.
   * Without it the gate's `review` cure — which every undated node carries, so
   * that nothing can go silent (law 1) — was the clock this returned, so a thing
   * nobody had dated printed "returns today" and SORTED TO THE FRONT of a sheet
   * titled *What comes back, and when*. The comment on the sort directly below
   * says undated things belong at the end, and it was right; nothing was
   * reaching that branch, because a cured node is never clockless.
   */
  const rowClock = (n: NodeState) => {
    const mine = [n.clocks.due, n.clocks.review, n.clocks.start, n.clocks.suspense, n.clocks.park]
      .find(c => c != null && !isAppClock(c));
    return mine ?? undefined;
  };

  function buildCoverage(): void {
    const state = session.state();
    // SORTED BY WHEN IT COMES BACK (3.19.3). Reported: this list is the answer
    // to "what is coming, and is it time to act on one", and it was ordered
    // newest-first by id — which is a fact about when a thing was WRITTEN and
    // says nothing about when it returns. The sheet's own title is "What comes
    // back, and when"; the second half of that sentence is now the order.
    //
    // Anything with no clock — the Menu, and a thing held with neither — sorts
    // to the END rather than the start. It has no return date to be early or
    // late against, and putting the undated first would bury the next thing due
    // beneath a wish list, which is exactly what law 6 keeps off the work
    // surface. Ties break on title so the order is stable between repaints.
    const held = [...heldWork(state)].sort((a, b) => {
      const ca = rowClock(a), cb = rowClock(b);
      if (ca && cb) {
        if (ca.at !== cb.at) return ca.at < cb.at ? -1 : 1;
        return (a.title || '').localeCompare(b.title || '');
      }
      if (ca) return -1;
      if (cb) return 1;
      return (a.title || '').localeCompare(b.title || '');
    });
    COVERAGE.replaceChildren(...held.map(n => {
      const li = el('li', 'coverage-item');
      // A door (1.6.0), still lazily built — the row count is why this list
      // builds on open, and a listener per row does not change that rule.
      const b = el('button', 'coverage-open');
      b.type = 'button';
      b.append(el('span', 'coverage-title', n.title || '(untitled)'));
      const clock = rowClock(n);
      // THE ROW ASKS `whyCovered` NOW, AND THAT IS THE POINT (3.23.16).
      //
      // This was a ternary — `onMenu ? … : clock ? … : 'held'` — which is a
      // SECOND statement of the precedence `whyCovered` exists to state, on the
      // same surface, three lines from the counts that use the first one. So the
      // two could be independently wrong about one row, and they were: a finished
      // thing counted under "with a day they come back to you" up there and read
      // "returns today" down here, and fixing either alone would have left the
      // surface contradicting itself.
      //
      // The comment this replaces recorded the identical bug in the identical
      // spot: the `on the Menu` arm was unreachable, because a Menu item keeps
      // the gate's own `review` cure and `clock` is therefore truthy for every
      // one of them. That was fixed by adding a branch — one more copy of the
      // order — rather than by asking the function that owns it. There is one
      // copy now, so the next state that has to win is added once.
      const why = whyCovered(n, state);
      b.append(el('span', 'coverage-when',
        why === 'menu' ? 'on the Menu'
          : why === 'done' ? 'finished'
            : why === 'clock' && clock ? `returns ${returns(clock.at)}`
              : 'held'));
      if (openDetail) b.addEventListener('click', () => {
        const fresh = session.state().nodes.get(n.id);
        if (!fresh) return;
        // The tree row's rule, for the same reason (2.0.5, ADR-0088).
        closeSheet('sheet-coverage');
        openDetail(fresh);
      });
      li.append(b);
      return li;
    }));
  }

  // --- the alignment tree, on request (1.6.0, ADR-0013/item 39) -------------
  // Queried SEPARATELY from the guard list above: a missing tree control must
  // cost the tree, never Next-up (the no-op-on-missing-selector trap).
  const treeOpen = q<HTMLButtonElement>('#tree-open');
  const treeList = q('#tree');
  /** Branches revealed past the cap THIS SITTING — memory only, like every
   *  reveal; the cap returns with the next visit. */
  const treeRevealed = new Set<string>();
  function buildTree(): void {
    if (!treeList) return;
    const rows = treeRows(session.state(), treeRevealed);
    treeList.replaceChildren(...rows.map(entry => {
      const li = el('li', 'tree-item');
      li.style.setProperty('--tree-depth', String(entry.depth));
      if (entry.kind === 'more') {
        const b = el('button', 'tree-more');
        b.type = 'button';
        b.textContent = entry.hidden === 1
          ? `1 more under ${entry.parent.title || '(untitled)'}`
          : `${entry.hidden} more under ${entry.parent.title || '(untitled)'}`;
        b.addEventListener('click', () => { treeRevealed.add(entry.parent.id); buildTree(); });
        li.append(b);
        return li;
      }
      const b = el('button', 'tree-open-row');
      b.type = 'button';
      b.append(el('span', 'tree-title', entry.node.title || '(untitled)'));
      // WHAT KIND OF THING EACH ROW IS (3.6.0). Three of the four surfaces that
      // list containers already say this — the held card via `held.ts`'s
      // `placeWords`, the sheet via `detail.ts`, and both pickers via
      // `containerOptionWords` — and the tree, the one surface whose whole job
      // is showing containers, drew every row as a bare indented title. So the
      // app teaches somebody the word `project` at the moment they make one and
      // at the moment they file something under one, and then the surface that
      // lists them never says it again.
      //
      // `nodeWords` returns null for `action`, so the unmarked case stays
      // unmarked and only the rows a reader cannot otherwise tell from an
      // action get named. Same function as the other two rows and the sheet, so
      // they cannot drift into four vocabularies — and it carries the one fact
      // that supersedes a kind, an answered waiting-for reading **Arrived**.
      const what = nodeWords(entry.node);
      if (what) b.append(el('span', 'tree-kind', what));
      if (openDetail) b.addEventListener('click', () => {
        const fresh = session.state().nodes.get(entry.node.id);
        if (!fresh) return;
        // THE INSPECTION SURFACE STEPS ASIDE (2.0.5, ADR-0088). A dialog opened
        // over a dialog is the overlap ADR-0083 forbids — the top one eats the
        // other's taps — so the tree closes as you walk through it.
        //
        // Closed HERE and not inside the detail sheet: `showNode` is also how
        // the sort conveyor hands you a card, and sort is a surface you come
        // BACK to. A blanket close there ends the sitting. Only an inspection
        // mode has nothing to come back to, so only an inspection mode closes.
        closeSheet('sheet-tree');
        openDetail(fresh);
      });
      li.append(b);
      return li;
    }));
    if (rows.length === 0) {
      treeList.append(el('li', 'tree-empty',
        'Nothing has a place inside anything else yet — the tree appears as things are filed.'));
    }
  }
  // AN INSPECTION MODE THAT STOPPED UNFOLDING INTO THE WORKSPACE (2.0.5,
  // ADR-0088). ADR-0013 called this "never the landing view; an inspection mode,
  // not a workspace" on the day it shipped, and it then expanded inline above
  // the held list for thirty-four releases — 17,246px of it on a full store.
  onSheetOpen('sheet-tree', buildTree);
  wireSheetClose('sheet-tree');
  treeOpen?.addEventListener('click', () => { openSheet('sheet-tree'); });

  refresh();
  return { refresh };
}
